import { orderBy } from 'lodash';
import {
  MetastructureFieldType,
  type CustomFieldDefinition,
  type CustomFieldGroupDefinition,
  type MetastructureField,
  type ResourceTypeModel
} from '__types__';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import {
  resourceTypeBindingsUtils,
  type ResourceTypeWorkspaceBinding
} from 'v5/api/resource-types/resource-type-bindings.api';
import {
  fieldSpecUtils,
  type EditUnit,
  type EntitySchema,
  type FieldSpec,
  type FieldsUnit,
  type ListUnit,
  type ResourceMembership,
  type ScopeSection
} from 'v5/core/entity-schema/entitySchema.types';
import {
  type BrowsingScope,
  type RowContainer,
  type ScopeSectionId,
  type SectionEditability,
  type UnitEditability,
  type UnitInheritance,
  type UnitOrigin,
  type ValueScope,
  type ListAddAction
} from 'v5/core/form-schema/formSchema.types';
import { mergeWorkspaceMetastructureFields } from 'v5/services/internal/ResourceTypeBindingsService/mergeWorkspaceMetastructureFields';
import type {
  ResolvedMetastructureField,
  ResolvedMetastructureFieldCore
} from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import {
  coreFieldKey,
  coreFieldSpec,
  customFieldSpec,
  type SpecOptions
} from './fieldSpecs';
import { rateListAddAction } from './rateListOptions';
import type { ResourceSchemaCopy } from './resourceSchemaCopy';
import { memberWorkspaceIds } from './resourceMembership';
import { resolveSectionEditability } from './resolveSectionEditability';

/**
 * Caller capabilities. `RESOURCE_UPDATE` drives section editability;
 * `RATE_READ` / `RATE_UPDATE` gate the rates list only (legacy
 * `ResourceRates` showed itself with at least one of the two).
 */
export type ResourceFormAccess = {
  canUpdateResource: boolean;
  /** Caller forces read-only (merge modal, previews). */
  forceReadOnly: boolean;
  /** `RATE_READ`: without it and without `RATE_UPDATE` the rates unit is omitted. */
  canReadRates: boolean;
  /** `RATE_UPDATE`: without it the rates unit is read-only (`no-capability`). */
  canUpdateRates: boolean;
};

/**
 * Core fields a host may present and edit itself, outside the form. Closed:
 * only fields that have a dedicated control somewhere in the product belong
 * here (the resource pane header renders group chips).
 */
export type DelegatedCoreField = 'groups';

export type ResolveResourceSchemaInput = {
  mode: 'edit' | 'create';
  resourceTypeId: number;
  resourceTypeModel: ResourceTypeModel;
  /**
   * Core fields the host renders and edits outside the form. The resolver
   * leaves them out of every section, so the form never duplicates a control
   * the surrounding chrome already shows. A host without such chrome (create
   * dialog, merge modal, previews) passes `[]` and gets the complete form.
   */
  presentedByHost: ReadonlyArray<DelegatedCoreField>;
  /** Org-level identity, from MetastructureMappingService. */
  globalMetastructure: ResolvedMetastructureField[];
  /** Specs for overlay-only definition ids and for group members. */
  customFieldDefinitions: Record<number, CustomFieldDefinition>;
  bindings: ResourceTypeWorkspaceBinding[];
  workspaces: EnterpriseWorkspace[];
  /** Stored (edit) or picked (create, §4.4). */
  membership: ResourceMembership;
  /** Edit: a workspace URL also shows that workspace's section for a non-member. */
  browsing: BrowsingScope;
  /** workspaceId → definition ids with a live override row (`collectOverrides`). Ignored in `create` mode. */
  overrides: ReadonlyMap<number, ReadonlySet<number>>;
  access: ResourceFormAccess;
  options: SpecOptions;
};

const GLOBAL_SCOPE: ValueScope = { type: 'global' };
const ENTITY_CONTAINER: RowContainer = { type: 'entity' };
const NOT_APPLICABLE: UnitInheritance = { type: 'not-applicable' };

type UnitContext = {
  copy: ResourceSchemaCopy;
  sectionId: ScopeSectionId;
  sectionEditability: SectionEditability;
  scope: ValueScope;
  origin: UnitOrigin;
};

/**
 * Unit editability = section editability, or `locked-by-type` when every
 * field of the unit is locked by the metastructure / binding.
 */
function unitEditability(
  section: SectionEditability,
  fields: ReadonlyArray<FieldSpec>
): UnitEditability {
  if (section.type === 'read-only') {
    return { type: 'read-only', reason: section.reason };
  }
  if (fields.length > 0 && fields.every(field => field.locked)) {
    return { type: 'read-only', reason: 'locked-by-type' };
  }
  return { type: 'editable' };
}

/** Rates: section reason first, then the `RATE_UPDATE` capability, then the type lock. */
function ratesUnitEditability(
  section: SectionEditability,
  fields: ReadonlyArray<FieldSpec>,
  access: ResourceFormAccess
): UnitEditability {
  if (section.type === 'read-only') {
    return { type: 'read-only', reason: section.reason };
  }
  if (!access.canUpdateRates) {
    return { type: 'read-only', reason: 'no-capability' };
  }
  return unitEditability(section, fields);
}

function fieldsUnit(
  ctx: UnitContext,
  id: string,
  title: string,
  container: RowContainer,
  fields: FieldSpec[]
): FieldsUnit {
  return {
    type: 'fields',
    id,
    title,
    scope: ctx.scope,
    origin: ctx.origin,
    inheritance: NOT_APPLICABLE,
    editability: unitEditability(ctx.sectionEditability, fields),
    container,
    fields
  };
}

function listUnit(
  ctx: UnitContext,
  id: string,
  title: string,
  container: RowContainer,
  item: ListUnit['item'],
  editability: UnitEditability,
  add: ListAddAction,
  reorderable: boolean
): ListUnit {
  return {
    type: 'list',
    id,
    title,
    scope: ctx.scope,
    origin: ctx.origin,
    inheritance: NOT_APPLICABLE,
    editability,
    container,
    item,
    add,
    reorderable
  };
}

/**
 * The unit of core field `name` (`global/core:full_name`): core fields only
 * have one, in Global. For hosts that open it from outside the form
 * (`SchemaSectionsProps.editUnitSignal`).
 */
export function globalCoreUnitId(name: string): string {
  return `global/${coreFieldKey(name)}`;
}

/**
 * Core columns / relations have no `enterprise_workspace_id`: always global
 * scope. Rates additionally honour the rate capabilities: neither `RATE_READ`
 * nor `RATE_UPDATE` → no unit at all; no `RATE_UPDATE` → read-only list.
 */
function coreUnit(
  ctx: UnitContext,
  field: ResolvedMetastructureFieldCore,
  options: SpecOptions,
  access: ResourceFormAccess
): EditUnit | null {
  const spec = coreFieldSpec(field, options);
  if (spec === null) return null;
  const id = `${ctx.sectionId}/${spec.key}`;
  const coreCtx: UnitContext = { ...ctx, scope: GLOBAL_SCOPE };
  if (spec.kind === 'rate') {
    if (!access.canReadRates && !access.canUpdateRates) return null;
    return listUnit(
      coreCtx,
      id,
      spec.label,
      ENTITY_CONTAINER,
      { title: ctx.copy.rateItemTitle, fields: [spec] },
      ratesUnitEditability(ctx.sectionEditability, [spec], access),
      rateListAddAction(
        options.rateCards,
        ctx.copy,
        options.rateCardsSettingsPath
      ),
      true
    );
  }
  if (spec.kind === 'location') {
    const item = ctx.copy.locationItemTitle;
    return listUnit(
      coreCtx,
      id,
      spec.label,
      ENTITY_CONTAINER,
      // Never required, as in v1: a cleared line is dropped on save, not blocked.
      { title: item, fields: [{ ...spec, required: false }] },
      unitEditability(ctx.sectionEditability, [spec]),
      { type: 'single', label: ctx.copy.addItem(item.toLowerCase()) },
      // The contact–location join stores no order yet.
      false
    );
  }
  return fieldsUnit(coreCtx, id, spec.label, ENTITY_CONTAINER, [spec]);
}

function customUnit(
  ctx: UnitContext,
  definition: CustomFieldDefinition,
  field: { required: boolean; locked: boolean }
): FieldsUnit {
  const spec = customFieldSpec(
    definition,
    definition.required || field.required,
    field.locked,
    ctx.copy
  );
  return fieldsUnit(
    ctx,
    `${ctx.sectionId}/${spec.key}`,
    spec.label,
    ENTITY_CONTAINER,
    [spec]
  );
}

/**
 * Single group → FieldsUnit, array group → ListUnit; members keep their own
 * `required`, the group's lock applies to all. Members follow their `order`
 * from the group join, which the backend returns unsorted.
 *
 * A member's copy nested in the group can lack `options` (the group's
 * create / update response doesn't fetch them) or be older than the field's
 * own definition, so specs come from `customFieldDefinitions` when it has the id.
 */
function groupUnit(
  ctx: UnitContext,
  definition: CustomFieldGroupDefinition,
  field: { locked: boolean },
  customFieldDefinitions: Record<number, CustomFieldDefinition>
): EditUnit {
  const container: RowContainer = {
    type: 'custom-field-group',
    definition
  };
  const members = orderBy(definition.custom_field_definitions, 'order').map(
    nested => {
      const member = customFieldDefinitions[nested.id] ?? nested;
      return customFieldSpec(member, member.required, field.locked, ctx.copy);
    }
  );
  const id = `${ctx.sectionId}/group:${definition.id}`;
  if (definition.is_array) {
    return listUnit(
      ctx,
      id,
      definition.name,
      container,
      { title: definition.name, fields: members },
      unitEditability(ctx.sectionEditability, members),
      { type: 'single', label: ctx.copy.addItem(definition.name) },
      true
    );
  }
  return fieldsUnit(ctx, id, definition.name, container, members);
}

function globalUnit(
  ctx: UnitContext,
  field: ResolvedMetastructureField,
  options: SpecOptions,
  access: ResourceFormAccess,
  customFieldDefinitions: Record<number, CustomFieldDefinition>
): EditUnit | null {
  switch (field.type) {
    case MetastructureFieldType.Core:
      return coreUnit(ctx, field, options, access);
    case MetastructureFieldType.Custom:
      return customUnit(ctx, field.customFieldDefinition, field);
    case MetastructureFieldType.CustomGroup:
      return groupUnit(
        ctx,
        field.customFieldGroupDefinition,
        field,
        customFieldDefinitions
      );
  }
}

/** Drop the resolved definition props so `mergeWorkspaceMetastructureFields` gets plain fields. */
function toMetastructureField(
  field: ResolvedMetastructureField
): MetastructureField {
  const base = {
    active: field.active,
    locked: field.locked,
    default: field.default,
    required: field.required
  };
  switch (field.type) {
    case MetastructureFieldType.Core:
      return { ...base, type: field.type, name: field.name };
    case MetastructureFieldType.Custom:
      return {
        ...base,
        type: field.type,
        custom_field_definition_id: field.custom_field_definition_id
      };
    case MetastructureFieldType.CustomGroup:
      return {
        ...base,
        type: field.type,
        custom_fieldgroup_definition_id: field.custom_fieldgroup_definition_id
      };
  }
}

function metastructureFieldKey(field: MetastructureField): string {
  switch (field.type) {
    case MetastructureFieldType.Core:
      return `core:${field.name}`;
    case MetastructureFieldType.Custom:
      return `custom:${field.custom_field_definition_id}`;
    case MetastructureFieldType.CustomGroup:
      return `group:${field.custom_fieldgroup_definition_id}`;
  }
}

/** A Global custom-field unit that gets a mirror in every workspace section. */
function mirrorableDefinitionOf(unit: EditUnit): CustomFieldDefinition | null {
  if (unit.type !== 'fields') return null;
  if (unit.container.type !== 'entity') return null;
  if (unit.fields.length !== 1) return null;
  return fieldSpecUtils.definitionOf(unit.fields[0]);
}

/**
 * The only place that knows the section / unit rules of proposal §4.1–§4.5.
 * Pure: metastructure + bindings + membership + browsing scope in, schema out.
 */
export function resolveResourceSchema(
  input: ResolveResourceSchemaInput
): EntitySchema {
  const options: SpecOptions = {
    ...input.options,
    resourceTypeId: input.resourceTypeId,
    resourceTypeModel: input.resourceTypeModel
  };
  const access = input.access;
  const presentedByHost: ReadonlySet<string> = new Set(input.presentedByHost);
  const activeFields = input.globalMetastructure.filter(
    field =>
      field.active &&
      !(
        field.type === MetastructureFieldType.Core &&
        presentedByHost.has(field.name)
      )
  );

  // --- Which workspace sections exist -------------------------------------
  const enabledWorkspaces = input.workspaces.filter(workspace =>
    resourceTypeBindingsUtils.isEnabledInWorkspace(
      input.bindings,
      input.resourceTypeId,
      workspace.id
    )
  );
  const sectionWorkspaces = sectionWorkspaceIds(
    input,
    enabledWorkspaces.map(workspace => workspace.id)
  ).flatMap(id => enabledWorkspaces.filter(workspace => workspace.id === id));

  // --- Workspace overlays (fields their bindings add to the type) ---------
  const globalPlainFields = activeFields.map(toMetastructureField);
  const globalKeys = new Set(globalPlainFields.map(metastructureFieldKey));
  const overlays = sectionWorkspaces.map(workspace => ({
    workspace,
    fields: mergeWorkspaceMetastructureFields(
      globalPlainFields,
      input.bindings,
      input.resourceTypeId,
      workspace.id
    ).fields.filter(field => !globalKeys.has(metastructureFieldKey(field)))
  }));
  // Core columns have no workspace scope (§4.1): a core field a binding adds
  // is shown once, in Global; the first section's binding sets its flags.
  const coreOverlayFields = new Map<string, ResolvedMetastructureFieldCore>();
  for (const overlay of overlays) {
    for (const field of overlay.fields) {
      if (field.type !== MetastructureFieldType.Core) continue;
      if (presentedByHost.has(field.name)) continue;
      if (!coreOverlayFields.has(field.name)) {
        coreOverlayFields.set(field.name, field);
      }
    }
  }

  // --- Global section ---------------------------------------------------
  const globalEditability = resolveSectionEditability({
    section: GLOBAL_SCOPE,
    browsing: input.browsing,
    canUpdateResource: access.canUpdateResource,
    forceReadOnly: access.forceReadOnly,
    workspaceArchived: false
  });
  const globalCtx: UnitContext = {
    copy: options.copy,
    sectionId: 'global',
    sectionEditability: globalEditability,
    scope: GLOBAL_SCOPE,
    origin: { type: 'global' }
  };
  const globalUnits: EditUnit[] = [];
  for (const field of activeFields) {
    const unit = globalUnit(
      globalCtx,
      field,
      options,
      access,
      input.customFieldDefinitions
    );
    if (unit !== null) globalUnits.push(unit);
  }
  for (const field of coreOverlayFields.values()) {
    const unit = coreUnit(globalCtx, field, options, access);
    if (unit !== null) globalUnits.push(unit);
  }
  const globalSection: ScopeSection = {
    id: 'global',
    title: input.options.copy.globalSection,
    scope: GLOBAL_SCOPE,
    editability: globalEditability,
    units: globalUnits,
    overriddenCount: 0,
    defaultExpanded: true
  };

  // --- Workspace sections -------------------------------------------------
  const workspaceSections: ScopeSection[] = [];
  for (const { workspace, fields } of overlays) {
    const workspaceId = workspace.id;
    const sectionId: ScopeSectionId = `ws:${workspaceId}`;
    const scope: ValueScope = { type: 'workspace', workspaceId };
    const sectionEditability = resolveSectionEditability({
      section: scope,
      browsing: input.browsing,
      canUpdateResource: access.canUpdateResource,
      forceReadOnly: access.forceReadOnly,
      workspaceArchived: workspace.archived
    });
    const overriddenIds =
      input.mode === 'create'
        ? null
        : (input.overrides.get(workspaceId) ?? null);

    // (a) mirrors of Global custom-field units, in Global order
    const mirrors: EditUnit[] = [];
    for (const unit of globalUnits) {
      if (unit.type !== 'fields') continue;
      const definition = mirrorableDefinitionOf(unit);
      if (definition === null) continue;
      const inheritance: UnitInheritance =
        overriddenIds !== null && overriddenIds.has(definition.id)
          ? { type: 'overridden' }
          : { type: 'inherited' };
      mirrors.push({
        ...unit,
        id: `${sectionId}/${unit.fields[0].key}`,
        scope,
        origin: { type: 'mirror', globalUnitId: unit.id },
        inheritance,
        editability: unitEditability(sectionEditability, unit.fields)
      });
    }

    // (b) overlay custom fields appended by the workspace binding; core
    // overlays went to Global, groups are never overlaid (§4.5)
    const overlayCtx: UnitContext = {
      copy: options.copy,
      sectionId,
      sectionEditability,
      scope,
      origin: { type: 'overlay' }
    };
    const overlayUnits: EditUnit[] = [];
    for (const field of fields) {
      if (field.type !== MetastructureFieldType.Custom) continue;
      const definition =
        input.customFieldDefinitions[field.custom_field_definition_id];
      if (definition === undefined) continue;
      overlayUnits.push(customUnit(overlayCtx, definition, field));
    }

    // Nothing can be stored per workspace here (no Global custom fields to
    // mirror, no overlay): the section would be an empty shell — omit it.
    if (mirrors.length === 0 && overlayUnits.length === 0) continue;

    workspaceSections.push({
      id: sectionId,
      title: workspace.name,
      scope,
      editability: sectionEditability,
      units: [...mirrors, ...overlayUnits],
      overriddenCount: mirrors.filter(
        unit => unit.inheritance.type === 'overridden'
      ).length,
      defaultExpanded:
        input.browsing.level === 'workspace' &&
        input.browsing.workspaceId === workspaceId
    });
  }

  return {
    entity: 'resource',
    mode: input.mode,
    sections: [globalSection, ...workspaceSections]
  };
}

/**
 * Workspaces that get a section, browsing workspace first: the member ones
 * (§4.1) and, in edit, the browsing workspace even for a non-member — legacy
 * forms showed and wrote its overlay fields under its URL. Create follows the
 * picked membership only. `enabledIds` sets the order of the rest.
 */
function sectionWorkspaceIds(
  input: ResolveResourceSchemaInput,
  enabledIds: ReadonlyArray<number>
): number[] {
  const memberIds = memberWorkspaceIds(input.membership, enabledIds);
  if (input.browsing.level === 'organization') return memberIds;
  const browsingId = input.browsing.workspaceId;
  const others = memberIds.filter(id => id !== browsingId);
  const browsingShown =
    memberIds.includes(browsingId) ||
    (input.mode === 'edit' && enabledIds.includes(browsingId));
  return browsingShown ? [browsingId, ...others] : others;
}
